import cloneDeep from 'lodash/cloneDeep';
import flatten from 'lodash/flatten';
import indexOf from 'lodash/indexOf';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import lodashMap from 'lodash/map';
import { Observable, Subject } from 'rxjs';
import { map, publishLast } from 'rxjs/operators';

import { Inject, Injectable } from '@angular/core';

import {
  CompanyProperty,
  IResponseCompany,
  IResponseCompanyConnection,
  IResponseCompanyConnectRoles,
  IResponseLoginPreferences,
  IResponseNestedGroup,
  IResponseUser,
  IResponseUserAccount,
} from '@services/company.interface';
import { GroupsFilter } from '@services/groups-filter';
import { GroupsService, IGroupMapping } from '@services/groups.service';
import { ServerApi } from '@services/server-api';
import { SessionStorage } from '@services/session-storage';
import { EntGroupHierarchyConfigService } from '@services/dynamic-configs/ent-group-hierarchy-enabled';
import { EnterpriseProductivityFeatureFlagService } from './feature-flags/enterprise-productivity';
import { GlobalSupportTimezonesService } from './global-support/global-support-timezones.service';
import { CoachingFeatureFlagService } from '@services/feature-flags/coaching';
import { SessionFeatureFlagService } from '@services/feature-flags/session';

import { ITimeZoneDropDown } from './global-support/global-support.interface';
import { CountryCodesService } from './global-support/global-support-country-codes.service';

import { ICountryCode } from './global-support/global-support.interface';
import { FLEET_CARD_APPLICATION_STATUS } from '@app/dashboard/fleet-cards/fleet-card-status';
import { AI_DRIVER_IDENTIFICATION_SETTINGS, CameraSettings } from '@app/admin/company/company.consts';
import { AssetMonitoringFeatureFlagService } from './feature-flags/asset-monitoring';
import { FleetCardsFeatureFlagService } from './feature-flags/fleet-cards';
import { ELD_MODES } from '@lib/eld-modes';
import { ENV } from '@lib/environments.const';
import { PLANS } from '@lib/plans';
import { DEFAULT_TIME_ZONE, TIME_ZONES } from '@lib/time-zones';

import { Role } from '@lib/roles.const';

import { cycleDays, getCycle, isCycleShortHaul } from '@app/cycle/cycles';
import { DISPATCH_UPSELL_STATUS } from '@app/dashboard/dispatch/dispatch-status';
import { moment } from '@lib/moment';
import { ALL_ACCESSIBLE_GROUP_SCOPE, PERMISSIONS, UserPermission, allPermissions } from '@lib/permissions';
import { inDemoMode, inSupportMode, inReadOnlyMode, updateReadOnlyModeCookie } from '@lib/remote-login';
import { getSessionData, setUserInfoToCookie } from '@lib/session-data';
import { mapLocale } from '@lib/map-locale';
import { METERS_PER_KM, extractResponseBody, fullName, phoneNumberToIntlFormat, phoneNumberToStandardFormat } from '@lib/utils';
import { setCookie } from '@app/lib/cookies';
import { CookieKey } from '@app/lib/cookie-keys.const';
import { SmartLoadBoardFeatureFlagService } from './feature-flags/smart-load-board';
import { UPSELL_CAMPAIGN_NAMES } from '@misc/upsell/upsell-config';
import { CARD_STATUS } from '@fleet-cards/main.constants';

export interface IPendingCounts {
  pending_drivers: number;
  pending_fleet_users: number;
  result: boolean;
  pending_coaching: number;
}

export enum SLBAccessFlags {
  FLEET_MANAGERS_FLAG = 'web_fleet_dashboard',
  DRIVERS_FLAG = 'mobile_driver_app',
}

export enum LoginPreferences {
  BASIC = 'basic',
  HYBRID = 'hybrid',
  SSO = 'sso',
}

const USER_SESSION_ID_KEY = 'userSessionId';
const NEW_TIMEZONE_MIN_APP_VERSION: number = 52;

@Injectable({ providedIn: 'root' })
export class Session {
  public pendingFleetUsers: number;
  public pendingDrivers: number;
  public sessionUpdate$: Observable<any> = new Observable<any>();
  public userPermissions: UserPermission;
  private sessionUpdateSubject: Subject<any> = new Subject<any>();
  private user: IResponseUser;

  constructor(
    @Inject(ServerApi) private api,
    private groupsFilter: GroupsFilter,
    private groupsService: GroupsService,
    private sessionStorage: SessionStorage,
    private fleetCardsFeatureFlagService: FleetCardsFeatureFlagService,
    private assetMonitoringFeatureFlagService: AssetMonitoringFeatureFlagService,
    private entGroupHierarchyConfigService: EntGroupHierarchyConfigService,
    private smartLoadBoardFeatureFlagService: SmartLoadBoardFeatureFlagService,
    private globalTzService: GlobalSupportTimezonesService,
    private epFeatureFlag: EnterpriseProductivityFeatureFlagService,
    private countryCodesService: CountryCodesService,
    private sessionFeatureFlagService: SessionFeatureFlagService,
    private coachingFeatureFlagService: CoachingFeatureFlagService,
  ) {
    this.userPermissions = new UserPermission(this);
    this.sessionUpdate$ = this.sessionUpdateSubject.asObservable();
    this.user = getSessionData();
    if (
      this.userPermissions.isDriversManageAllowed() ||
      this.userPermissions.isFleetUsersManageAllowed()
    ) {
      this.updatePendingCount();
    }

    if (this.getCompanyProperty('tpms_enabled') && this.getCompanyEnabledFeatures()) {
      this.getCompanyEnabledFeatures().push('tpms');
    }

    setUserInfoToCookie(
      this.isCompanyPaidPlan(),
      this.getCompanyProperty('subscription_plan'),
      this.companyHasAssetGateways(),
      this.hasCompanyVideoMonitoringEnabled(),
      this.getCompanyProperty('composite_fleet_size'),
    );

    if (this.user.is_partner_user) {
      setCookie(CookieKey.READ_ONLY_MODE, 'yes');
      updateReadOnlyModeCookie();
    }
  }

  public refresh() {
    const req$ = this.api.get('sessions/validate')
      .pipe(
        map(extractResponseBody),
        map((res: any): IResponseUser => res.user),
        publishLast(),
      );
    req$.subscribe((user: IResponseUser): void => {
      this.user = user;
      this.sessionUpdateSubject.next({ ...user });
    });
    req$.connect();
    return req$;
  }

  public getUser(): IResponseUser { return this.user; }

  public getUserId(): number { return this.user.id; }

  public getUserFullName(): string { return fullName(this.user); }

  public getUserRole(): string { return this.user.role; }

  public getUserPhone(): string { return this.user.phone; }

  public getUserEmail(): string {
    return this.user.connected_user && this.user.primary_user ? this.user.primary_user.email : this.user.email;
  }

  public getUserSessionId(): string {
    let sessionId: string = this.sessionStorage.getKey(USER_SESSION_ID_KEY);
    if (sessionId) return sessionId;

    const timeStamp = new Date();
    const userId = this.user.id;
    const specialChars = /[-:.]/g;
    sessionId = (`${userId}${timeStamp.toISOString()}`).replace(specialChars, '');
    this.sessionStorage.setKey(USER_SESSION_ID_KEY, sessionId);
    return sessionId;
  }
  public getUserFirstName(): string { return this.user.first_name; }

  public getUserLastName(): string { return this.user.last_name; }

  public getUserCreationDate(): string { return this.user.created_at; }

  public getUserLanguage(): string { return mapLocale(this.user.language); }

  public isDispatcher(): boolean { return this.getUserRole() === Role.DISPATCHER; }

  public isDriver(): boolean { return this.getUserRole() === Role.DRIVER; }

  public isAdmin(): boolean {
    return this.isDispatcher() && this.isCompanyAdmin();
  }

  public isCompanyAdmin(): boolean {
    const connection: IResponseCompanyConnection = this.getCompanyConnection();
    return connection && connection.role === Role.ADMIN;
  }

  public getUserEnabledFeatures(): string[] {
    const connection: IResponseCompanyConnection = this.getCompanyConnection();
    return connection ? this.getEnabledPermissions(connection) : [];
  }

  public isFeatureAllowed(feature: string): boolean {
    const permission = allPermissions.find((perm: string) => feature === perm);
    return indexOf(this.getUserEnabledFeatures(), permission) > -1;
  }

  public getAvailableTimezones(): { [key: string]: string; } {
    return cloneDeep(TIME_ZONES);
  }

  public getAvailableGlobalTimezones(): ITimeZoneDropDown[] {
    return this.globalTzService.timeZoneList;
  }

  public getStandardTimeZone(identifier: string): string {
    const standardTimeZone =
    this.globalTzService.standardTimeZonesMapper[identifier]?.localizedStandardNameWithGMTOffset || identifier;
    return this.epFeatureFlag.isGlobalTimezonesEnabled() ? standardTimeZone : identifier;
  }

  public getStandardUserTimeZone(): string {
    const tz = this.getUser().time_zone || DEFAULT_TIME_ZONE;
    return this.epFeatureFlag.isGlobalTimezonesEnabled() ? this.getStandardTimeZone(tz) : tz;
  }

  public hasOlderAppVersionForNewTimeZones(appVersion?: number): boolean {
    if (!appVersion || !this.epFeatureFlag.isGlobalTimezonesEnabled()) return false;

    return appVersion < this.globalTzService.getNewTimeZoneMinAppVersion();
  }

  public isDispatchDriverWorkflowEnabled(): boolean {
    return this.getCompanyProperty('kt_driver_workflow_enabled');
  }

  public isDispatchPublicDriverWorkflowEnabled(): boolean {
    return this.getCompanyProperty('enabled_features').includes('driver_workflow') && !this.isDispatchDriverWorkflowEnabled();
  }

  public isMappingRfidDriverIdEnabled(): boolean {
    return this.isCompanyFeatureEnabled('badge_detection_mode');
  }

  public isCardHoldersReadEnabled(): boolean {
    return this.userPermissions.isCardHoldersReadAllowed();
  }

  public isCoachLogsManageAllowed(): boolean {
    return this.userPermissions.isCoachLogsManageAllowed();
  }

  public isCoachSafetyEventsManageAllowed(): boolean {
    return this.userPermissions.isCoachSafetyEventsManageAllowed();
  }

  public isMotiveAnalyticsReadAllowed(): boolean {
    return this.userPermissions.isMotiveAnalyticsReadAllowed();
  }

  public isMotiveAnalyticsEditAllowed(): boolean {
    return this.userPermissions.isMotiveAnalyticsManageAllowed();
  }

  public isCoachingHubAllowed(): boolean {
    return this.isCoachLogsManageAllowed() || this.isCoachSafetyEventsManageAllowed();
  }

  public isVehiclesManageAllowed(): boolean {
    return this.userPermissions.isVehiclesManageAllowed();
  }

  public isDriverAndVehicleDocumentManageAllowed(): boolean {
    return this.userPermissions.isDriverAndVehicleDocumentManageAllowed();
  }

  public isCardTransactionReadEnabled(): boolean {
    return this.userPermissions.isCardTransactionReadAllowed();
  }

  public isCardMissedSavingsReadEnabled(): boolean {
    return this.userPermissions.isCardMissedSavingsReadAllowed();
  }

  public isCardTransactionManageEnabled(): boolean {
    return this.userPermissions.isCardTransactionManageAllowed();
  }

  public isPaymentsAndInvoicesReadEnabled(): boolean {
    return this.userPermissions.isPaymentsAndInvoicesReadAllowed();
  }

  public isBanksAndPaymentsManageEnabled(): boolean {
    return this.userPermissions.isBanksAndPaymentsManageAllowed();
  }

  public isCardManagementReadEnabled(): boolean {
    return this.userPermissions.isCardManagementReadAllowed();
  }

  public isCardManagementManageEnabled(): boolean {
    return this.userPermissions.isCardManagementManageAllowed();
  }

  public isCardConfigurationManageEnabled(): boolean {
    return this.fleetCardsFeatureFlagService.isEmailPreferencesEnabled() ?
      this.userPermissions.isCardConfigurationManageAllowed() : this.isCardManagementManageEnabled();
  }

  public isCardsAdminReadEnabled(): boolean {
    return this.userPermissions.isCardsAdminReadAllowed();
  }

  public isTrainingManageAllowed(): boolean {
    return this.userPermissions.isTrainingManageAllowed();
  }

  public isTrainingReadAllowed(): boolean {
    return this.userPermissions.isTrainingReadAllowed();
  }

  public hasFleetCardAdministrativePermissionEnabled(): boolean {
    return this.userPermissions.hasFleetCardAdministrativePermission();
  }

  public isFleetCardAdminNavItemEnabled(): boolean {
    return ((this.isAdmin() && (this.getFleetCardApplicationStatus() === FLEET_CARD_APPLICATION_STATUS.WAITLIST ||
      this.getFleetCardApplicationStatus() === FLEET_CARD_APPLICATION_STATUS.UPSELL)) ||
      (this.getFleetCardApplicationStatus() === FLEET_CARD_APPLICATION_STATUS.APPROVED
        && this.hasFleetCardAdministrativePermissionEnabled()));
  }

  public hasFleetCardDashboardPermissionEnabled(): boolean {
    return this.userPermissions.hasFleetCardDashboardPermission();
  }

  public hasActiveFleetUserCard(): boolean {
    return [CARD_STATUS.active, CARD_STATUS.suspended].includes(this.user.fleet_card_status as CARD_STATUS);
  }

  public hasPermissionToOnlyMarkOrDismissEvents(): boolean {
    return this.coachingFeatureFlagService.isCoachingPermissionsByRoleEnabled()
      && (this.userPermissions.isAllowedToMarkEventsCoachable()
      || this.userPermissions.isDismissSafetyEventsManageAllowed())
      && !this.userPermissions.isAllowedToConductEventsCoaching();
  }

  public hasPermissionToOnlyConductEventsCoaching(): boolean {
    return this.coachingFeatureFlagService.isCoachingPermissionsByRoleEnabled()
      && this.userPermissions.isAllowedToConductEventsCoaching()
      && !this.userPermissions.isAllowedToMarkEventsCoachable()
      && !this.userPermissions.isDismissSafetyEventsManageAllowed();
  }

  public hasDismissAndConductEventsCoachingPermissionOnly(): boolean {
    return this.coachingFeatureFlagService.isCoachingPermissionsByRoleEnabled()
      && this.userPermissions.isDismissSafetyEventsManageAllowed()
      && this.userPermissions.isAllowedToConductEventsCoaching()
      && !this.userPermissions.isAllowedToMarkEventsCoachable();
  }

  public hasPermissionToOnlyMarkLogs(): boolean {
    return this.coachingFeatureFlagService.isCoachingPermissionsByRoleEnabled()
      && this.userPermissions.isAllowedToMarkLogsCoachable()
      && !this.userPermissions.isAllowedToConductLogsCoaching();
  }

  public hasPermissionToOnlyConductLogsCoaching(): boolean {
    return this.coachingFeatureFlagService.isCoachingPermissionsByRoleEnabled()
      && this.userPermissions.isAllowedToConductLogsCoaching()
      && !this.userPermissions.isAllowedToMarkLogsCoachable();
  }

  public isFleetCardDashboardNavItemEnabled(): boolean {
    return (!this.isDriver() &&
      (this.isFleetCardEnabled() && this.isFleetCardsUpsellRolesAndPermissionsEnforced() &&
        (this.getFleetCardApplicationStatus() === FLEET_CARD_APPLICATION_STATUS.WAITLIST ||
          this.getFleetCardApplicationStatus() === FLEET_CARD_APPLICATION_STATUS.UPSELL)) ||
      (this.getFleetCardApplicationStatus() === FLEET_CARD_APPLICATION_STATUS.APPROVED
        && this.hasFleetCardDashboardPermissionEnabled()));
  }

  public isFleetCardsUpsellRolesAndPermissionsEnforced(): boolean {
    return this.fleetCardsFeatureFlagService.isFleetCardsUpsellRolesAndPermissionsEnabled() ?
      this.hasFleetCardDashboardPermissionEnabled() : true;
  }

  public isDispatchReadEnabled(): boolean {
    return this.userPermissions.isDispatchReadAllowed();
  }

  public isDispatchManageEnabled(): boolean {
    return this.userPermissions.isDispatchManageAllowed();
  }

  public isMaintenanceReminderEnabled(): boolean {
    return this.isCompanyFeatureEnabled('maintenance') && this.userPermissions.isMaintenanceManageAllowed();
  }

  public isAuxEnabled(): boolean {
    return this.getCompanyProperty('aux_enabled');
  }

  public isPTOEnabled(): boolean {
    return this.getCompanyProperty('contains_pto_vehicles');
  }

  public isSLBOptOut(): boolean {
    return this.getCompanyProperty('slb_opt_out');
  }

  public isCompanyDataRetentionEnabled(): boolean {
    return this.getCompanyProperty('data_retention_enabled');
  }

  public getUserMetricUnits(): boolean {
    return this.getUser().metric_units;
  }

  public getCompanyMetricUnits(): boolean {
    return this.getCompany().metric_units;
  }

  public getUserFuelEconomyUnits(): string {
    return this.getUser().fuel_economy_unit;
  }

  public getUserTimeZone() {
    const tz = this.getUser().time_zone || DEFAULT_TIME_ZONE;
    if (this.epFeatureFlag.isGlobalTimezonesEnabled()) {
      return this.globalTzService.getIANAIdentifier(tz) || tz;
    }
    return tz;
  }

  public getCountries(): ICountryCode[] {
    return this.countryCodesService.countries;
  }

  public getCountryByCode(code: string): ICountryCode {
    return this.countryCodesService.getCountryByCode(code);
  }

  public getStateNameByCode(code: string): string {
    return this.countryCodesService.getStateByCode(code)?.name || '';
  }

  public getCountryNameByStateCode(code: string): string {
    const countryCode = this.countryCodesService.getStateByCode(code)?.countryCode;
    return countryCode ? this.getCountryByCode(countryCode)?.name || '' : '';
  }

  public get defaultCountry(): ICountryCode {
    const countryIso = this.getCompanyCountry() || 'US';
    return this.getCountryByCode(countryIso);
  }

  public getGroupVisibility() {
    const connection = this.getCompanyConnection();
    return connection && connection.group_visibility;
  }

  public getCompanyConnection(): IResponseCompanyConnection {
    if (this.getUser().company_connection) {
      return this.getUser().company_connection;
    }
  }

  public getCompany(): IResponseCompany {
    const connection: IResponseCompanyConnection = this.getCompanyConnection();
    if (connection && connection.company) {
      return connection.company;
    }
  }

  public getCompanyProperty(propName: CompanyProperty): any {
    const company: IResponseCompany = this.getCompany();
    return (company && company[propName]);
  }

  public showDriverInLocationHistoryByVehicleReport(): boolean {
    return this.getCompanyProperty('show_driver_vehicle_location_history_report');
  }

  public isCompanyEldStarter(): boolean {
    return this.getCompanyProperty('subscription_plan') === PLANS.ELD_STARTER;
  }

  public isCompanyFaceMatchNewSettingEnabled(): boolean {
    const aiDriverIdentificationSetting = this.getCompanyProperty('compliance_ai_driver_suggestion');

    return this.isCompanyAboveStarter() && (aiDriverIdentificationSetting === AI_DRIVER_IDENTIFICATION_SETTINGS.FACIAL_RECOGNITION
    || aiDriverIdentificationSetting === AI_DRIVER_IDENTIFICATION_SETTINGS.TELEMATICS_FACIAL);
  }

  public isCompanyEldPlus(): boolean {
    return this.getCompanyProperty('subscription_plan') === PLANS.ELD_PLUS;
  }

  public isCompanyStarterPlan(): boolean {
    return this.getCompanyProperty('subscription_plan') === PLANS.STARTER;
  }

  public isCompanyBetterPlan(): boolean {
    return this.getCompanyProperty('subscription_plan') === PLANS.BETTER;
  }

  public isCompanyBestPlan(): boolean {
    return this.getCompanyProperty('subscription_plan') === PLANS.BEST;
  }

  public isCompanyPaidPlan(): boolean {
    return (Object.values(PLANS).indexOf(this.getCompanyProperty('subscription_plan')) > -1);
  }

  public isCompanyAboveStarter(): boolean {
    return !(this.isCompanyEldStarter() || this.isCompanyStarterPlan());
  }

  public isCompanyLegacyPlan(): boolean {
    return this.isCompanyEldStarter() || this.isCompanyEldPlus();
  }

  public companyHasCellularEnabledElds(): boolean {
    return this.getCompanyProperty('num_cell_eld_devices') > 0;
  }

  public isFuelTheftDetectionEnabled(): boolean {
    return this.getCompanyProperty('enable_fuel_theft_detection');
  }

  public isFleetViewAllowed(): boolean {
    return (
      this.isDispatcher() &&
      (this.isVehicleFeatureAllowed() ||
        this.isDriverFeatureAllowed() ||
        this.isAssetFeatureAllowed() ||
        this.userPermissions.isTripsManageAllowed())
    );
  }

  public isVideoMonitoringEnabled(): boolean {
    return this.hasCompanyVideoMonitoringEnabled() && this.isFeatureAllowed(PERMISSIONS.VIDEO_MONITORING_READ);
  }

  public isImageMonitoringEnabled(): boolean {
    if (this.isVideoMonitoringEnabled()) return true; // If video monitoring is enabled, image monitoring is also enabled

    return this.hasCompanyVideoMonitoringEnabled() && this.isFeatureAllowed(PERMISSIONS.IMAGE_MONITORING_READ);
  }

  public areCompanyDriversExempt(): boolean {
    const areSelectedGroupExempted = this.groupsFilter.areSelectedGroupExempted;
    if (areSelectedGroupExempted === null) {
      return this.getCompanyProperty('operating_eld_modes') === 'exempt';
    } else {
      return areSelectedGroupExempted;
    }
  }

  public areCompanyDriversMixed(): boolean {
    return this.getCompanyProperty('operating_eld_modes') === 'mixed';
  }

  public areCompanyDriversLogs(): boolean {
    const mode = this.getCompanyProperty('operating_eld_modes');
    return mode === 'logs' || mode === 'none';
  }

  public isSubscriptionPlanAssigned(): boolean {
    return !!this.getCompanyProperty('subscription_plan');
  }

  public isSSOEnabledForCompany(): boolean {
    return this.getCompanyProperty('sso_enabled');
  }

  public getCompanyLoginPreferences(): IResponseLoginPreferences {
    return this.getCompanyProperty('login_preferences');
  }

  public getLoginPreferenceByRole(userRole: string): LoginPreferences {
    const loginPreferences: IResponseLoginPreferences = this.getCompanyLoginPreferences();
    return loginPreferences && loginPreferences[userRole];
  }

  public getCompanyEnabledFeatures(): string[] {
    return this.getCompanyProperty('enabled_features');
  }

  public isReverseGeocodingEnabled(): boolean {
    return this.getCompanyProperty('reverse_geocoding_enabled');
  }

  public isDriverClaimedTripIconEnabled(): boolean {
    return this.getCompanyProperty('claimed_driving_period_indicator_enabled');
  }

  public getFleetCardApplicationStatus(): FLEET_CARD_APPLICATION_STATUS {
    return this.getCompanyProperty('fleet_card_application_status');
  }

  public isFleetCardEnabled(): boolean {
    return (this.getCompanyProperty('fleet_card_application_status') !== FLEET_CARD_APPLICATION_STATUS.INACTIVE &&
    this.getCompanyProperty('fleet_card_application_status') !== FLEET_CARD_APPLICATION_STATUS.BLOCKLISTED);
  }

  public isFleetCardApproved(): boolean {
    return this.getFleetCardApplicationStatus() === FLEET_CARD_APPLICATION_STATUS.APPROVED;
  }

  public isCompanyFeatureEnabled(feature: string): boolean {
    const companyFeatures: string[] = this.getCompanyEnabledFeatures() || [];
    const hijackOnlyFeatures: string[] = [];

    return companyFeatures.includes(feature) &&
      (!hijackOnlyFeatures.includes(feature) ||
        ![ENV.PRODUCTION, ENV.STAGING].includes(ENVIRONMENT as ENV) ||
        inSupportMode());
  }

  public userHasEmail(): boolean {
    const email = this.getUserEmail();
    return typeof email === 'string' && email.length > 0;
  }

  public hasCompanyIftaEnabled(): boolean {
    return this.isCompanyFeatureEnabled('ifta');
  }

  // This flag is responsible for the whole Safety feature
  public hasCompanyDriverPerformanceEnabled(): boolean {
    return this.isCompanyFeatureEnabled('driver_performance');
  }

  public isDriverPerformanceEnabled(): boolean {
    return this.hasCompanyDriverPerformanceEnabled() && this.userPermissions.isDriverPerformanceManageAllowed();
  }

  public isDriveScoreEnabled(): boolean {
    return this.getCompanyProperty('safety_overview_enabled');
  }

  public isChatAllowed(): boolean {
    return this.userPermissions.isMessageManageAllowed() &&
      !inSupportMode() && !inDemoMode() && !inReadOnlyMode() && this.isDispatcher()
    ;
  }

  public isStopSignViolationHighSeverityEnabled(): boolean {
    return this.getCompanyProperty('stop_sign_violation_default_high_severity');
  }

  public isAutomatedCoachingEnabled(): boolean {
    return this.getCompanyProperty('automated_coaching_enabled');
  }

  public hasCompanyDriverCoachingEnabled(): boolean {
    return this.getCompanyProperty('driver_coaching_enabled');
  }

  public hasCompanyDriverSafetyScoreVisible(): boolean {
    return this.getCompanyProperty('drive_for_embedded_webview_enabled');
  }

  public hasCompanyVideoMonitoringEnabled(): boolean {
    return this.isCompanyFeatureEnabled('video_monitoring');
  }

  public hasCompanyFaultCodesEnabled(): boolean {
    return this.isCompanyFeatureEnabled('fault_codes');
  }

  public hasCompanyIdlingEnabled(): boolean {
    return this.isCompanyFeatureEnabled('idling');
  }

  public hasCompanyFuelPurchaseEnabled(): boolean {
    return this.isCompanyFeatureEnabled('fuel_purchase');
  }

  public hasCompanyGeofenceEnabled(): boolean {
    return this.isCompanyFeatureEnabled('geofence');
  }

  public hasCompanyWifiHotspotEnabled(): boolean {
    return this.isCompanyFeatureEnabled('wifi_hotspot');
  }

  public hasCompanyThermokingEnabled(): boolean {
    return this.isCompanyFeatureEnabled('thermoking');
  }

  public hasCompanyEnvironmentalSensorEnabled(): boolean {
    return this.isCompanyFeatureEnabled('environmental_sensor');
  }

  public hasCompanyBeaconFeatureEnabled(): boolean {
    return this.isCompanyFeatureEnabled('beacon');
  }

  public hasCompanyWFMDriverQualificationsEnabled(): boolean {
    return this.isCompanyFeatureEnabled('driver_qualification');
  }

  public hasCompanyWFMTrainingEnabled(): boolean {
    return this.isCompanyFeatureEnabled('training');
  }

  public hasVehicleEIEnabled(): boolean {
    return this.isCompanyFeatureEnabled('vehicle_ei_enabled');
  }

  public hasCompanyAGReeferMonitoringEnabled(): boolean {
    return this.hasCompanyThermokingEnabled() || this.hasCompanyEnvironmentalSensorEnabled();
  }

  public hasCompanyRecallEnabled(): boolean {
    return this.isVideoMonitoringEnabled() && this.getCompanyProperty('video_recall_enabled');
  }

  public hasCompanyCanadianOutputFileEnabled(): boolean {
    return this.getCompanyProperty('canadian_mandate_output_files_enabled');
  }

  public hasCompanyLiveImageCheckEnabled(): boolean {
    return this.getCompanyProperty('image_check_enabled');
  }

  public hasCompanyTPMSSupportEnabled(): boolean {
    return this.getCompanyProperty('tpms_enabled');
  }

  public isEventIntelligenceEnabled(): boolean {
    return this.isVideoMonitoringEnabled() && this.getCompanyProperty('annotation_tag_enabled');
  }

  public isCompanyEventIntelligenceEnabled(): boolean {
    return this.hasCompanyVideoMonitoringEnabled() && this.getCompanyProperty('annotation_tag_enabled');
  }

  public companyLongIdlingThreshold(): number {
    return this.getCompanyProperty('is_long_idling_settings_configured')
      && this.getCompanyProperty('long_idling_threshold_mins');
  }

  public canadianCycleSwitchingEnabled(): boolean {
    return this.getCompanyProperty('canada_cycle_switching_requirement_enabled');
  }

  public exemptDrivingConfigEnabled(): boolean {
    return this.getCompanyProperty('exempt_driving_configuration_enabled');
  }

  public autoAnnotateUDEsInGeofenceEnabled(): boolean {
    return this.getCompanyProperty('auto_annotate_udes_in_geofences_enabled');
  }

  public hasCompanyWaitTime(): boolean {
    if (this.getCompanyProperty('drivers_with_exception_wait_time')) {
      return this.getCompany().drivers_with_exception_wait_time;
    }
    return false;
  }

  public hasPublicApiEnabled(): boolean {
    if (this.getCompany() && this.getCompany().api_enabled) {
      return !!this.getCompany().api_enabled;
    }
    return false;
  }

  public isSlbMobileDriverAppEnabled(): boolean {
    const slbMobileDriverAppFeatureFlags: string = this.getCompany().slb_access;
    return (slbMobileDriverAppFeatureFlags === `${SLBAccessFlags.FLEET_MANAGERS_FLAG},${SLBAccessFlags.DRIVERS_FLAG}` ||
           slbMobileDriverAppFeatureFlags === SLBAccessFlags.DRIVERS_FLAG)
           && !this.smartLoadBoardFeatureFlagService.isSlbAccessShutdownEnabled();
  }

  public hasLoadBoardEnabled(): boolean {
    return this.getCompanyProperty('slb_access') &&
      this.getCompanyProperty('slb_access').indexOf(SLBAccessFlags.FLEET_MANAGERS_FLAG) > -1 &&
      !this.isSLBOptOut();
  }

  public isSmartLoadBoardFeatureAllowed(): boolean {
    return this.isDispatcher() &&
      this.hasLoadBoardEnabled() &&
      this.userPermissions.isSmartLoadBoardReadAllowed() &&
      !this.smartLoadBoardFeatureFlagService.isSlbAccessShutdownEnabled();
  }

  public getAccountManager(): IResponseUserAccount {
    const accountManager = this.getCompanyProperty('account_manager');
    if (accountManager) {
      if (this.epFeatureFlag.isGlobalCountryCodesEnabled()) {
        accountManager.phone = phoneNumberToIntlFormat(accountManager.phone);
      } else {
        accountManager.phone = phoneNumberToStandardFormat(accountManager.phone);
      }
    }
    return accountManager;
  }

  public getAccountExecutive(): IResponseUserAccount {
    const accountExecutive = this.getCompanyProperty('account_executive');
    if (accountExecutive) {
      if (this.epFeatureFlag.isGlobalCountryCodesEnabled()) {
        accountExecutive.phone = phoneNumberToIntlFormat(accountExecutive.phone);
      } else {
        accountExecutive.phone = phoneNumberToStandardFormat(accountExecutive.phone);
      }
    }
    return accountExecutive;
  }

  public assignedElds(): number {
    if (this.getCompanyProperty('assigned_elds')) {
      return this.getCompany().assigned_elds;
    }
    return 0;
  }

  public unassignedElds(): number {
    if (this.getCompanyProperty('unassigned_elds')) {
      return this.getCompany().unassigned_elds;
    }
    return 0;
  }

  public assignedAssetGateways(): number {
    if (this.getCompanyProperty('assigned_assets')) {
      return this.getCompany().assigned_assets;
    }
    return 0;
  }

  public unassignedAssetGateways(): number {
    if (this.getCompanyProperty('unassigned_assets')) {
      return this.getCompany().unassigned_assets;
    }
    return 0;
  }

  public eldEnabledDrivers(): number {
    if (this.getCompanyProperty('eld_enabled_drivers')) {
      return this.getCompany().eld_enabled_drivers;
    }
    return 0;
  }

  public isCompanyComplianceEld(): boolean {
    return this.getCompanyProperty('compliance_mode') === 'eld';
  }

  public isCompanyComplianceAobrd(): boolean {
    return this.getCompanyProperty('compliance_mode') === 'aobrd';
  }

  public getTotalElds(): number {
    return this.assignedElds() + this.unassignedElds();
  }

  public companyHasElds(): boolean {
    return (this.assignedElds() + this.unassignedElds()) > 0;
  }

  public companyHasUnassignedElds(): boolean {
    return this.unassignedElds() > 0;
  }

  public companyHasAssetGateways(): boolean {
    return (this.assignedAssetGateways() + this.unassignedAssetGateways()) > 0;
  }

  public getUserCycle() { return this.getUser().cycle; }

  public getCycleDays() {
    const cycle = this.isDispatcher() ? this.getCompany().cycle : this.getUserCycle();
    return cycleDays(cycle);
  }

  public addNewAllowedGroup(group: IResponseNestedGroup): void {
    this.user.groups.push(group);
  }

  public getAllowedGroups(): IResponseNestedGroup[] {
    // check if flag is enabled or if groups exist for user
    // if groups doesn't exist return undefined
    if (
      !this.entGroupHierarchyConfigService.isGroupHierarchyEnabled() ||
      !this.getUser().groups
    ) {
      return this.getUser().groups || [];
    }

    const decendentGroups: IGroupMapping = this.getGroupChildrenMapping();

    return this.getUser().groups.reduce((acc: IResponseNestedGroup[], response: IResponseNestedGroup) => {
      if (decendentGroups?.[response.group.id]) {
        decendentGroups[response.group.id].forEach((decendentGroup: number) => {
          acc.push({
            group: {
              id: decendentGroup,
              name: '',
            },
          });
        });
      }

      return acc;
    }, [...this.getUser().groups]);
  }

  public getGroupChildrenMapping(): IGroupMapping {
    return this.groupsService.groupsChildrenMapping;
  }

  public isUserCycleOther(): boolean {
    return this.getUserCycle() === 'Other';
  }

  public isUserCycleShortHaul(): boolean {
    return isCycleShortHaul(this.getUser().cycle);
  }

  public hasRestrictedSettings(): boolean {
    return this.isDriver() && this.getCompany() && !this.getCompanyProperty('allow_drivers_to_edit_settings');
  }

  // Check if the passed group ids are one of the allowed group ids of the user
  public isInAllowedGroups(groupIds: number | number[], permission?: PERMISSIONS): boolean {
    if (!permission && typeof groupIds === 'number') {
      return lodashMap(this.getAllowedGroups(), 'group.id').includes(groupIds);
    }
    const groupVisibility = this.getGroupVisibility();
    if (groupVisibility !== 'all') {
      const userGroupIds: number[] = this.getGroupIdsOfPermission(permission);
      const commonGroups: number[] = permission === ALL_ACCESSIBLE_GROUP_SCOPE ? groupIds :
        intersection(groupIds, userGroupIds);
      return !!commonGroups.length;
    } else {
      return true;
    }
  }

  // Check if the passed group ids are in the selected groups. If the selected groups array is empty then compare
  // with the allowed group ids of the user.
  public isInSelectedGroups(groupIds: number[], permission?: PERMISSIONS): boolean {
    const groupsFilterValue: number[] = this.groupsFilter.value || [];
    const selectedGroupIds: number[] = groupsFilterValue.concat(
      flatten(groupsFilterValue.map(groupId => this.getGroupChildrenMapping()[groupId] || [])),
    );
    if (isEmpty(selectedGroupIds)) {
      return this.isInAllowedGroups(groupIds, permission);
    } else {
      const commonGroups = intersection(groupIds, selectedGroupIds);
      return !!commonGroups.length;
    }
  }

  public isAssetFeatureAllowed(): boolean {
    return this.isFeatureAllowed(PERMISSIONS.ASSETS_READ);
  }

  public isVehicleFeatureAllowed(): boolean {
    return this.isFeatureAllowed(PERMISSIONS.VEHICLES_READ);
  }

  public isDriverFeatureAllowed(): boolean {
    return this.isFeatureAllowed(PERMISSIONS.DRIVERS_READ);
  }

  public areAssetAndVehicleFeatureAllowed(): boolean {
    return this.isVehicleFeatureAllowed() && this.isAssetFeatureAllowed();
  }

  public areAssetLocationsFeatureAllowed(): boolean {
    return this.isFeatureAllowed(PERMISSIONS.ASSETS_LOCATIONS_READ);
  }

  public areVehicleLocationsFeatureAllowed(): boolean {
    return this.isFeatureAllowed(PERMISSIONS.VEHICLES_LOCATIONS_READ);
  }

  public areDriverLocationsFeatureAllowed(): boolean {
    return this.isFeatureAllowed(PERMISSIONS.DRIVERS_LOCATION_READ);
  }

  public areAssetAndVehicleLocationsAllowed(): boolean {
    return this.areAssetLocationsFeatureAllowed() && this.areVehicleLocationsFeatureAllowed();
  }

  public isRecommendedAppNotificationHidden(): boolean {
    return this.user.recommended_app_notification_hidden;
  }

  public updatePendingCount() {
    this.api
      .get('sessions/pending_connections')
      .pipe(
        map(extractResponseBody),
      )
      .subscribe(response => {
        if (this.userPermissions.isDriversManageAllowed()) {
          this.pendingDrivers = response.pending_drivers;
        }
        if (this.userPermissions.isFleetUsersManageAllowed()) {
          this.pendingFleetUsers = response.pending_fleet_users;
        }
      });
  }

  public getPendingCounts(): Observable<IPendingCounts> {
    return this.api
      .get('sessions/pending_connections')
      .pipe(
        map(extractResponseBody),
      );
  }

  public isHOSSortingEnabled(): boolean {
    return this.getCompany().hos_sorting_enabled;
  }

  public canReclassifyDrivingEvent(): boolean {
    return this.getCompanyProperty('sds_reclassification_enabled');
  }

  public isSpeedingEnabled(): boolean {
    return this.getCompanyProperty('speeding_enabled');
  }

  public isCompanyDriverMatchOn(): boolean {
    return this.getCompanyProperty('driver_match') === CameraSettings.On;
  }

  public getRoadTripImageBreadcrumb(): CameraSettings {
    return this.getCompanyProperty('road_image_breadcrumb');
  }

  public getCompanyZip(): string {
    return this.getCompanyProperty('zip');
  }

  public getCompositeFleetSize(): number {
    return this.getCompanyProperty('composite_fleet_size');
  }

  public eldEngineOffCheckinMode(): boolean {
    return this.getCompanyProperty('set_eld_engine_off_checkin_mode_on');
  }

  public getAccountStatus(): string {
    return this.getCompanyProperty('sf_account_status');
  }

  public isTrialCustomer(): boolean {
    return this.getCompanyProperty('sf_trial_status');
  }

  public isDevelopmentEnvironment(): boolean {
    return DEV_FEATURES_ENABLED;
  }

  public get proTrialStartDate(): string {
    return this.getCompanyProperty('dashboard_upsell_pro_trial_start_date');
  }

  public get proTrialEndDate(): string {
    return this.getCompanyProperty('dashboard_upsell_pro_trial_end_date');
  }

  public get featureTrialRemainingDays(): number {
    const currentDate = moment();
    const proTrialEndDate = moment(this.proTrialEndDate);
    return proTrialEndDate.diff(currentDate, 'days');
  }

  public getUserCompanyRole(): string {
    return this.user.survey_data.company_role;
  }

  public getPersonaSurveyAttempts(): number {
    return this.user.survey_data.survey_attempts;
  }

  public getPersonaSurveyLastSubmitDate(): string {
    return this.user.survey_data.survey_last_submitted;
  }

  public getIndustry(): string {
    return this.getCompanyProperty('industry');
  }

  public getFreight(): string[] {
    return this.getCompanyProperty('freight_carried');
  }

  public getUpgradeStatus(): any {
    return this.user.dashboard_upsell_status || {};
  }

  public get isGeneralDashcamGetTheDealRequested(): boolean {
    return !!this.user.dashboard_upsell_status.general_dashcam_upsell;
  }

  public get isFleetviewDashcamGetTheDealRequested(): boolean {
    return !!this.user.dashboard_upsell_status.fleetview_dashcam_upsell;
  }

  public get isCardsUpsellPopupRequested(): boolean {
    return !!this.user.dashboard_upsell_status.cards_upsell_pop_up;
  }

  public get isUpgradeRequested(): boolean {
    return !!this.user.dashboard_upsell_status.upgrade_now;
  }
  // TODO Temporary solution - check both campaign name and key
  public get isFleetCardUpsellRequested(): boolean {
    return !!this.user.dashboard_upsell_status.fleet_cards ||
        !!this.user.dashboard_upsell_status[UPSELL_CAMPAIGN_NAMES.fleet_cards]; // checks campaign name
  }

  public get isActivateSavingRequested(): boolean {
    return !!this.user.dashboard_upsell_status.smb_promo;
  }

  public get isGetDemoRequested(): boolean {
    return !!this.user.dashboard_upsell_status.get_demo_ai_dashcam;
  }

  public get isGetAQuoteRequested(): boolean {
    return !!this.user.dashboard_upsell_status.get_quote;
  }

  public get isTrialRequested(): boolean {
    return !!this.user.dashboard_upsell_status.request_trial;
  }

  public get pcLimitThreshold(): number {
    return this.getCompanyProperty('pc_limit_km_threshold') * METERS_PER_KM;
  }

  public get pcLimitJurisdiction(): string {
    return this.getCompanyProperty('pc_limit_jurisdiction');
  }

  public get hasProTrialEndDate(): boolean {
    return this.proTrialEndDate !== null;
  }

  public isFeatureTrial(): boolean {
    const currentDate = moment();
    const proTrialStartDate = moment(this.proTrialStartDate);
    const proTrialEndDate = moment(this.proTrialEndDate);
    return currentDate.isBetween(proTrialStartDate, proTrialEndDate);
  }

  public isTrialEnded(): boolean {
    const currentDate = moment();
    const proTrialEndDate = moment(this.proTrialEndDate);
    return currentDate.isAfter(proTrialEndDate);
  }

  public getEnabledPermissions(connection: IResponseCompanyConnection): string[] {
    return connection.permissions ? Object.keys(connection.permissions) : [];
  }

  public getGroupIdsOfPermission(permission: PERMISSIONS): number[] {
    const connection: IResponseCompanyConnection = this.getCompanyConnection();
    if (!connection) return [];
    return this.includeChildGroups(connection.permissions[permission]);
  }

  public includeChildGroups(groupIds: number[]): number[] {
    if (!this.entGroupHierarchyConfigService.isGroupHierarchyEnabled() || !groupIds) return groupIds;

    const decendentGroups: IGroupMapping = this.getGroupChildrenMapping();
    return groupIds.reduce((acc: number[], groupId: number) => {
      return acc.concat(decendentGroups?.[groupId] || []);
    }, groupIds);
  }

  public isFVPredictiveTrackingAllowed(): boolean {
    return !(this.isCompanyEldStarter() || this.isCompanyStarterPlan());
  }

  public isBendixEnabled(): boolean {
    return this.getCompanyProperty('bendix_enabled');
  }

  public companyHasFuelInsightsEnabled(): boolean {
    return this.getCompanyProperty('fuel_insights_enabled');
  }

  public hideDeactivatedDriverProfile(): boolean {
    return !!(this.getCompanyProperty('hide_deactivated_driver_profile'));
  }

  public isVehicleAvailabilitySimplifedVersionEnabled(): boolean {
    return !!(this.getCompanyProperty('vehicle_availability_simplified_version_enabled'));
  }

  public isOldOverviewEnabled(): boolean {
    return this.isDriver();
  }

  public isComplianceHubEnabled(): boolean {
    return !this.isDriver() &&
      (
        this.userPermissions.isLogsReadAllowed() ||
        this.userPermissions.isTripsManageAllowed() ||
        this.isComplianceCSAMonthlyEnabled() ||
        this.userPermissions.isEldDisconnectsAllowed()
      );
  }

  public isComplianceCSAMonthlyEnabled(): boolean {
    return this.userPermissions.isCSAInsightsAllowed();
  }

  public isUnregulatedVehicleProfilesAllowed(): boolean {
    return this.getCompanyProperty('allow_unregulated_vehicle_profiles');
  }

  public isThirdSignatureEnabled(): boolean {
    return this.getCompanyProperty('dvir_third_signature_enabled');
  }

  public isCompanySwitcherEnabled(): boolean {
    const connection: IResponseCompanyConnection = this.getCompanyConnection();
    return (
      connection &&
      (connection.role === Role.ADMIN || this.isInstallerRole())
    );
  }

  public isInstallerRole(): boolean {
    if (!this.getCompanyConnection()) return false;
    return this.getCompanyConnection().company_connection_user_roles
    .some((item: IResponseCompanyConnectRoles): boolean => item.company_connection_user_role.user_role.name === 'Installer');
  }

  public hasCompanyCustomDVIREnabled(): boolean {
    return this.isCompanyFeatureEnabled('custom_dvir');
  }

  /**
   * We hides safety score app wide based
   * on this flag (but we do calculate it)
   */
  public isSafetyScoreHidden(): boolean {
    return this.getCompanyProperty('disable_safety_scores');
  }

  public isDriverExempt(): boolean {
    return this.user.eld_mode === ELD_MODES.EXEMPTED;
  }

  public isSortByHOSAllowed(): boolean {
    return this.getCompanyProperty('sort_drivers_by_hos');
  }

  public isIdleTelematicsDataEnabled(): boolean {
    return this.getCompanyProperty('idle_telematics_data');
  }

  public isAssetCustomTypeEnabled(): boolean {
    return this.getCompanyProperty('asset_custom_type_enabled');
  }

  public isAIDriverIdentificationEnabled(): boolean {
    const aiDriverIdentificationSetting = this.getCompanyProperty('compliance_ai_driver_suggestion');

    return aiDriverIdentificationSetting !== 'none';
  }

  public isComplianceCSAActivityAllowed(): boolean {
    return (
      true
      // && this.userPermissions.isCSAActivityAllowed() && will be added once backend is done
    );
  }

  public getCompanyCountry(): string {
    return this.getCompanyProperty('country');
  }

  public isCanadianFleet(): boolean {
    return this.getCompanyProperty('country') === 'CA';
  }

  public isMexicanFleet(): boolean {
    return this.getCompanyProperty('country') === 'MX';
  }

  public isAmericanFleet(): boolean {
    return this.getCompanyProperty('country') === 'US';
  }

  public isComplianceIgnoreDriveTimeEnabled(): boolean {
    return this.userPermissions.isLogsIgnoreDriveTimeAllowed();
  }

  public ignoreDriveTimeAppVersion(): number {
    // 10000 is added to ensure ingore drive time is not available for user with smaller app versions
    return this.getUser().ignore_drive_time_app_version || 10000;
  }

  public get isSweepStakesRequested(): boolean {
    return !!this.user.dashboard_upsell_status.smb_cards_sweepstakes;
  }

  public getDispatchUpsellStatus(): DISPATCH_UPSELL_STATUS {
    return this.getCompanyProperty('dispatch_pricing_experiment_status') || 'purchased';
  }

  public isSafetyAIRecapEnabled(): boolean {
    return !!this.getCompany().safety_ai_recap_enabled;
  }

  public isEligibleForBilling(): boolean {
    return !!this.getCompany().billing_eligible;
  }

  public isEligibleForShop(): boolean {
    return !!this.getCompany()?.shop_eligible;
  }

  public getEcommEligibility(): string {
    return this.getCompany()?.ecomm_eligibility;
  }

  public isLegalNoticeAccepted(): boolean {
    return false;
  }

  public isFrenchLanguage(): boolean {
    return this.getUserLanguage() === 'fr-CA';
  }

  public isSpanishLanguage(): boolean {
    return this.getUserLanguage() === 'es-MX';
  }

  public isVideoRecallManagePermissionEnabled(): boolean {
    return this.sessionFeatureFlagService.isVideoRecallManagePermissionEnabled();
  }

  public isSafetyBehaviorTagsPermissionEnabled(): boolean {
    return this.sessionFeatureFlagService.isSafetyBehaviorTagsPermissionEnabled();
  }

  public isRemoteLoginVideoDownloadEnabled(): boolean {
    return this.sessionFeatureFlagService.isRemoteLoginVideoDownloadEnabled();
  }

  public isFullDriverPrivacyEnabled(): boolean {
    return this.sessionFeatureFlagService.isFullDriverPrivacyEnabled() && this.userPermissions.isDriverPrivacyManageAllowed();
  }
}
